
import Matchup, { MatchupResult } from "@/shared/matchups/matchup";
import orderBy from "lodash/orderBy";
import Owner from "@/shared/owners/owner";
import OwnerTeam from "@/shared/teams/owner-team";
import { ref } from "vue";
import Season from "@/shared/seasons/season";
import Team from "@/shared/teams/team";
import { useStore } from 'vuex';
import { watch } from 'vue';

function consolidateSeasons(seasons: Season[]): MatchupResult[] {
  const matchups: MatchupResult[] = [];
  const ownerTeams: OwnerTeam[] = [];
  const date = new Date();
  const currentYear =
    date.getMonth() > 2 ? date.getFullYear() : date.getFullYear() - 1;
  let memberCount = 1;

  function getOwnerTeam(
    ownerTeams: OwnerTeam[],
    opponentId: number,
    season: number
  ): OwnerTeam {
    const baseOwnerTeam = {
      id: 0,
      owner: {
        id: "0",
        firstName: "BYE",
        lastName: "WEEK",
      },
    };

    if (opponentId === 0) return baseOwnerTeam;

    for (let i = 0; i < ownerTeams.length; i++) {
      const ownerTeam = ownerTeams[i];
      const teamYear = ownerTeam.teamYears && ownerTeam.teamYears.find((teamYear) => {
        return teamYear.year === season && teamYear.team === opponentId;
      });

      if (teamYear) return ownerTeam;
    }

    return baseOwnerTeam;
  }

  function getMatchupScores(
    matchup: Matchup,
    teamSide: string,
    season: Season
  ): void {
    if (!matchup[teamSide]) return;

    for (const week in matchup[teamSide].pointsByScoringPeriod) {
      let opponentSide = "home";
      if (teamSide === "home") opponentSide = "away";
      
      const currentTeam: OwnerTeam = getOwnerTeam(ownerTeams, matchup[teamSide].teamId, season.seasonId);

      const opponent: OwnerTeam = getOwnerTeam(
        ownerTeams,
        (matchup[opponentSide] && matchup[opponentSide].teamId) || 0,
        season.seasonId
      );

      let result: string;

      if (matchup.winner === teamSide.toUpperCase()) {
        result = "W";
      } else if (matchup.winner === "TIE") {
        result = "T";
      } else if (matchup.winner === "UNDECIDED") {
        result  = "U";
      } else {
        result = "L";
      }

      if (matchup[teamSide].pointsByScoringPeriod) {
        const score: MatchupResult = {
          margin: 0,
          points: matchup[teamSide].totalPoints,
          pointsByScoringPeriod: matchup[teamSide].pointsByScoringPeriod[week],
          pointsAgainst: matchup[opponentSide] && matchup[opponentSide].totalPoints,
          pointsAgainstByScoringPeriod: matchup[opponentSide] && matchup[opponentSide].pointsByScoringPeriod && matchup[opponentSide].pointsByScoringPeriod[week],
          opponent: opponent,
          result: result,
          team: currentTeam,
          week: parseInt(week),
          year: season.seasonId,
        };

        score.margin = (score.pointsByScoringPeriod || 0) - (score.pointsAgainstByScoringPeriod || 0);

        matchups.push(score);
      }
    }
  }

  for (let i = 0; i < seasons.length; i++) {
    const season = seasons[i];
    for (let k = 0; k < season.members.length; k++) {
      const member: Owner = season.members[k];

      if (!ownerTeams.some((x) => x.owner.id === member.id)) {
        memberCount++;
        const ownerTeam: OwnerTeam = {
          id: memberCount,
          currentSeasonGames: {
            pointsFor: 0,
            pointsAgainst: 0,
            wins: 0,
            losses: 0,
            ties: 0,
            matchupCount: 0,
          },
          recentGames: {
            pointsFor: 0,
            pointsAgainst: 0,
            wins: 0,
            losses: 0,
            ties: 0,
            matchupCount: 0,
          },
          matchupHistory: [],
          owner: member,
          teams: [],
          teamYears: [],
          highScore: {
            points: 0,
          },
          lowScore: {
            points: 500,
          },
          championshipYears: [],
          playoffYears: [],
          playoffWins: 0,
          experience: 0,
        };
        ownerTeams.push(ownerTeam);
      }
    }
  }

  for (let i = 0; i < seasons.length; i++) {
    // todo this loop exists in multiple places so it may be abvle to be a service that can only run one time when the site is loaded/leagueid is switched
    const season: Season = seasons[i];

    for (let j = 0; j < season.teams.length; j++) {
      const team: Team = season.teams[j];

      for (let i = 0; i < ownerTeams.length; i++) {
        const ownerTeam: OwnerTeam = ownerTeams[i];
        if (ownerTeam.owner.id === team.owners[0] && ownerTeam.teams && ownerTeam.teamYears) {
          ownerTeam.teams.push(team);
          ownerTeam.teamYears.push({ year: season.seasonId, team: team.id });
          if (season.seasonId !== currentYear) {
            if (!ownerTeam.experience) ownerTeam.experience = 0;
            ownerTeam.experience++;
          }
        }
      }
    }

    for (
      let matchupIndex = 0;
      matchupIndex < season.schedule.length;
      matchupIndex++
    ) {
      const matchup: Matchup = season.schedule[matchupIndex];
      getMatchupScores(matchup, "away", season);
      getMatchupScores(matchup, "home", season);
    }
  }

  return orderBy(matchups, ["pointsByScoringPeriod", "year", "week"], ["desc", "asc", "asc"]);
}

function countScores(matchups:  MatchupResult[]) {
  type ScoreCounter = { [index: number]: any; points: number; count: number };
  const scores: ScoreCounter = {} as ScoreCounter;

  for (let i = 0; i < matchups.length; i++) {
    const matchup: MatchupResult = matchups[i];
    if (!scores[matchup.points]) {
      scores[matchup.points] = {
        points: matchup.points,
        count: 0,
      };
    }

    scores[matchup.points].count++;
  }

  return orderBy(scores, ["count", "points"], ["desc", "desc"]);
}

function getMedianNumber(array: number[]) {
  const mid = Math.floor(array.length / 2);
  const nums = [...array].sort((a, b) => a - b);
  return array.length % 2 !== 0 ? nums[mid] : (nums[mid - 1] + nums[mid]) / 2;
}

function getAverage(array: number[]) {
  return array.reduce((a: number, b: number) => a + b) / array.length;
}

function getWeeklyMedianScores(matchups: MatchupResult[]) {
  type ScoreCounter = {[index: string]: any; week: number; year: number; median: number };
  const scores: ScoreCounter = { } as ScoreCounter;

  for (let i = 0; i < matchups.length; i++) {
    const matchup = matchups[i];

    if (!scores[matchup.week + '-' + matchup.year]) {
      scores[matchup.week + '-' + matchup.year] = {
        week: matchup.week,
        year: matchup.year, 
        scores: [],
        median: 0,
      };
    }

    scores[matchup.week + '-' + matchup.year].scores.push(matchup.pointsByScoringPeriod);
  }

  for (const weeklyScore in scores) {
    scores[weeklyScore].median = parseFloat(getMedianNumber(scores[weeklyScore].scores).toFixed(2));
  }

  return orderBy(scores, ["median", "year", "week"], ["desc", "asc", "asc"]);
}

function getWeeklyAverageScores(matchups: MatchupResult[]) {
  type ScoreCounter = {[index: string]: any; week: number; year: number; average: number };
  const scores: ScoreCounter = { } as ScoreCounter;

  for (let i = 0; i < matchups.length; i++) {
    const matchup = matchups[i];

    if (!scores[matchup.week + '-' + matchup.year]) {
      scores[matchup.week + '-' + matchup.year] = {
        week: matchup.week,
        year: matchup.year, 
        scores: [],
        average: 0,
      };
    }

    scores[matchup.week + '-' + matchup.year].scores.push(matchup.pointsByScoringPeriod);
  }

  for (const weeklyScore in scores) {
    scores[weeklyScore].average = parseFloat(getAverage(scores[weeklyScore].scores).toFixed(2))
  }

  return orderBy(scores, ["average", "year", "week"], ["desc", "asc", "asc"]);  
}

function getYearlyAverageScores(matchups: MatchupResult[]) {
  type ScoreCounter = {[index: string]: any; year: number; average: number };
  const scores: ScoreCounter = { } as ScoreCounter;

  for (let i = 0; i < matchups.length; i++) {
    const matchup = matchups[i];

    if (!scores[matchup.year]) {
      scores[matchup.year] = {
        year: matchup.year, 
        scores: [],
        average: 0,
      };
    }

    scores[matchup.year].scores.push(matchup.pointsByScoringPeriod);
  }

  for (const yearlyScore in scores) {
    scores[yearlyScore].average = parseFloat(getAverage(scores[yearlyScore].scores).toFixed(2))
  }

  return orderBy(scores, ["average", "year"], ["desc", "asc"]);  
}

function getYearlyMedianScores(matchups: MatchupResult[]) {
  type ScoreCounter = {[index: string]: any; year: number; median: number };
  const scores: ScoreCounter = { } as ScoreCounter;

  for (let i = 0; i < matchups.length; i++) {
    const matchup = matchups[i];

    if (!scores[matchup.year]) {
      scores[matchup.year] = {
        year: matchup.year, 
        scores: [],
        median: 0,
      };
    }

    scores[matchup.year].scores.push(matchup.pointsByScoringPeriod);
  }

  for (const yearlyScore in scores) {
    scores[yearlyScore].median = parseFloat(getMedianNumber(scores[yearlyScore].scores).toFixed(2));
  }

  return orderBy(scores, ["median", "year"], ["desc", "asc"]);
}

function getLowestWeeklyScores(matchups: MatchupResult[], unordered?: boolean) {
  type ScoreCounter = {[index: string]: any; week: number; year: number; lowest: number; scores: number[] };
  const scores: ScoreCounter = { } as ScoreCounter;

  for (let i = 0; i < matchups.length; i++) {
    const matchup = matchups[i];

    if (!scores[matchup.year + '-' + matchup.week]) {
      scores[matchup.year + '-' + matchup.week] = {
        year: matchup.year, 
        week: matchup.week,
        scores: [],
        lowest: 0,
      };
    }

    scores[matchup.year + '-' + matchup.week].scores.push(matchup.pointsByScoringPeriod);
  }

  for (const weeklyScore in scores) {
    scores[weeklyScore].lowest = Math.min(...scores[weeklyScore].scores);
  }

  if (unordered) return scores;

  return orderBy(scores, ["lowest", "year", "week"], ["desc", "asc", "asc"]);
}

function getHighestWeeklyScores(matchups: MatchupResult[]) {
  type ScoreCounter = {[index: string]: any; week: number; year: number; highest: number; scores: number[] };
  const scores: ScoreCounter = { } as ScoreCounter;

  for (let i = 0; i < matchups.length; i++) {
    const matchup = matchups[i];

    if (!scores[matchup.year + '-' + matchup.week]) {
      scores[matchup.year + '-' + matchup.week] = {
        year: matchup.year, 
        week: matchup.week,
        scores: [],
        highest: 0,
      };
    }

    scores[matchup.year + '-' + matchup.week].scores.push(matchup.pointsByScoringPeriod);
  }

  for (const weeklyScore in scores) {
    scores[weeklyScore].highest = Math.max(...scores[weeklyScore].scores);
  }

  return orderBy(scores, ["highest", "year", "week"], ["asc", "asc", "asc"]);
}

function getMarginOfVictories(matchups: MatchupResult[]) {
  const victoryMargins = matchups.slice();
  const filtered = victoryMargins.filter(x =>  x.margin && x.margin >= 0 && x.points > 0 && x.pointsAgainst > 0);

  return orderBy(filtered, ["margin", "year", "week"], ["desc", "asc", "asc"]);
} 

function getWeeklyScores(matchups: MatchupResult[]): {[index: string]: any; weeklyMatchups: MatchupResult[] } {
  type ScoreCounter = {[index: string]: any; weeklyMatchups: MatchupResult[] };
  const scores: ScoreCounter = { } as ScoreCounter;

  for (let i = 0; i < matchups.length; i++) {
    const matchup = matchups[i];

    if (!scores[matchup.year + '-' + matchup.week]) {
      scores[matchup.year + '-' + matchup.week] = {
        weeklyMatchups: [],
      };
    }    
    
    scores[matchup.year + '-' + matchup.week].weeklyMatchups.push(Object.assign({}, matchup));
  }

  return scores;
}

function getWeeklyMargins(matchups: MatchupResult[]) {
  type ScoreCounter = {[index: string]: any; week: number; year: number; margin: number; scores: number[] };
  const scores: ScoreCounter = { } as ScoreCounter;
  const highScores: any = getHighestWeeklyScores(matchups);
  const lowScores: any = getLowestWeeklyScores(matchups, true);

  for (let i = 0; i < highScores.length; i++) {
    const high = highScores[i];
    const low = lowScores[high.year + '-' + high.week];

    scores[high.year + '-' + high.week] = {
      year: high.year,
      week: high.week,
      margin: parseFloat((high.highest - low.lowest).toFixed(2)),
    }
  }

  return orderBy(scores, ['margin', 'year', 'week'], ['asc', 'asc', 'asc']);
}

function getLuckyWinners(matchups: MatchupResult[]) {
  const luckyWinners = [];
  const scores = getWeeklyScores(matchups);

  for (const score in scores) {
    let min = Infinity; 
    let secondMin = Infinity; 
    let secondMinIndex = undefined;
    let minIndex = undefined;
    for (let j = 0; j < scores[score].weeklyMatchups.length; j++) {
      const weeklyMatchup = scores[score].weeklyMatchups[j];

      if (weeklyMatchup.points < min) {
        secondMinIndex = minIndex;
        secondMin = min;
        min = weeklyMatchup.points; 
        minIndex = j;
      } else if (weeklyMatchup.points < secondMin) {
        secondMin = weeklyMatchup.points; 
        secondMinIndex = j;
      }
    }

    if (secondMinIndex && scores[score].weeklyMatchups[secondMinIndex].opponent.id > 0 && scores[score].weeklyMatchups[secondMinIndex].result === "W") {
      luckyWinners.push(scores[score].weeklyMatchups[secondMinIndex]);
    }
  }

  return orderBy(luckyWinners, ["year", "week"], ["desc", "desc"]);
}

function getUnluckyLosers(matchups: MatchupResult[]) {
  const unluckyLosers = [];
  const scores = getWeeklyScores(matchups);

  for (const score in scores) {
    let max = 0; 
    let secondMax = 0; 
    let secondMaxIndex = undefined;
    let maxIndex = undefined;
    for (let j = 0; j < scores[score].weeklyMatchups.length; j++) {
      const weeklyMatchup = scores[score].weeklyMatchups[j];

      if (weeklyMatchup.points > max) {
        secondMaxIndex = maxIndex;
        secondMax = max;
        max = weeklyMatchup.points; 
        maxIndex = j;
      } else if (weeklyMatchup.points > secondMax) {
        secondMax = weeklyMatchup.points; 
        secondMaxIndex = j;
      }
    }

    if (secondMaxIndex && scores[score].weeklyMatchups[secondMaxIndex].opponent.id > 0 && scores[score].weeklyMatchups[secondMaxIndex].result === "L") {
      unluckyLosers.push(scores[score].weeklyMatchups[secondMaxIndex]);
    }
  }

  return orderBy(unluckyLosers, ["year", "week"], ["desc", "desc"]);
}

function getLowestScoringWinners(matchups: MatchupResult[]) {
  const lowestScoringWinners = matchups.slice();
  const winners = lowestScoringWinners.filter(x => x.result === "W");
  return orderBy(winners, ["pointsByScoringPeriod", "year", "week"], ["asc", "desc", "desc"]);
}

function getHighestScoringLosers(matchups: MatchupResult[]) {
  const highestScoringLosers = matchups.slice();
  const losers = highestScoringLosers.filter(x => x.opponent.id && x.result === "L" && x.opponent.id > 0);
  return orderBy(losers, ["pointsByScoringPeriod", "year", "week"], ["desc", "desc", "desc"]);
}

function useHistory() {
  const matchups = ref<MatchupResult[]>([]);
  const boardType = ref('score');
  const scores = ref({});
  const weeklyMedianScores = ref({});
  const weeklyAverageScores = ref({});
  const yearlyMedianScores = ref({});
  const yearlyAverageScores = ref({});
  const lowestWeeklyScores = ref({});
  const highestWeeklyScores = ref({});
  const victoryMargins = ref({});
  const weeklyMargins = ref({});
  const luckyWinners = ref({});
  const unluckyLosers = ref({});
  const highestScoringLosers = ref({});
  const lowestScoringWinners = ref({});
  const store = useStore();
  const selectedBoardText = { 
    'score': 'Highest Scores',
    'modalscore': 'Modal Scores',
    'medianweeks': 'Weekly Median',
    'weeklyaverage': 'Weekly Average',
    'medianyears': 'Yearly Median',
    'yearlyaverage': 'Yearly Average',
    'highestlow': 'Highest Low Score',
    'lowesthigh': 'Lowest High Score',
    'victorymargins': 'Victory Margins',
    'weeklymargins': 'Weekly Margins',
    'luckywinners': 'Lucky Winners',
    'unluckylosers': 'Unlucky Losers',
    'highestloser': 'Highest Losers',
    'lowestwinner': 'Lowest Winner',
  }

  async function getLeagueHistory() {
    matchups.value = [];
    scores.value = {};
    weeklyMedianScores.value = {};
    weeklyAverageScores.value = {};
    yearlyMedianScores.value = {};
    yearlyAverageScores.value = {};
    lowestWeeklyScores.value = {};
    highestWeeklyScores.value = {};
    victoryMargins.value = {};
    weeklyMargins.value = {};
    luckyWinners.value = {};
    unluckyLosers.value = {};
    lowestScoringWinners.value = {};
    highestScoringLosers.value = {};

    matchups.value = consolidateSeasons(store.state.league) as MatchupResult[];

    scores.value = countScores(matchups.value);
    weeklyMedianScores.value = getWeeklyMedianScores(matchups.value);
    weeklyAverageScores.value = getWeeklyAverageScores(matchups.value);
    yearlyMedianScores.value = getYearlyMedianScores(matchups.value);
    yearlyAverageScores.value = getYearlyAverageScores(matchups.value);
    lowestWeeklyScores.value = getLowestWeeklyScores(matchups.value);
    highestWeeklyScores.value = getHighestWeeklyScores(matchups.value);
    victoryMargins.value = getMarginOfVictories(matchups.value);
    weeklyMargins.value = getWeeklyMargins(matchups.value);
    luckyWinners.value = getLuckyWinners(matchups.value);
    unluckyLosers.value = getUnluckyLosers(matchups.value);
    lowestScoringWinners.value = getLowestScoringWinners(matchups.value);
    highestScoringLosers.value = getHighestScoringLosers(matchups.value);
  }

  watch(
    () => store.state.leagueId,
    () => {
      getLeagueHistory();
    }
  )

  watch(
    () => store.state.league,
    () => {
      getLeagueHistory();
    }
  )

  getLeagueHistory();

  return { 
    boardType, 
    matchups, 
    scores, 
    selectedBoardText,
    weeklyMedianScores, 
    yearlyMedianScores,
    weeklyAverageScores,
    yearlyAverageScores,
    lowestWeeklyScores,
    highestWeeklyScores,
    victoryMargins,
    weeklyMargins,
    luckyWinners,
    unluckyLosers,
    highestScoringLosers,
    lowestScoringWinners,
  };
}

export default {
  setup() {
    return useHistory();
  },
};
